import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { navigate } from "../../../../../../navigation/RootNavigation";

const formatTenant = (tenant) => ({
  id: tenant?.app_user_id || "",
  title: tenant?.app_user?.user?.title || "",
  name: tenant?.app_user?.user?.first_name || "",
  surname: tenant?.app_user?.user?.last_name || "",
  email: tenant?.app_user?.user?.email || "",
  code: "+44",
  phone: tenant?.app_user?.user?.contact_number || "",
  role: tenant?.is_lead ? "Lead Tenant" : "Joint Tenant",
});

const TenantRoleCards = ({ tenants, openflow_id, address, onAddressChange }) => {
  const [formData, setFormData] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  useEffect(() => {
    if (tenants?.length > 0) {
      const formatted = {};
      tenants.forEach((t) => {
        const key = String(t?.app_user_id ?? t.id);
        formatted[key] = formatTenant(t);
      });

      setFormData(formatted);
      setSelectedTenantId(String(tenants[0]?.app_user_id ?? tenants[0]?.id));
    }
  }, [tenants]);

  if (!tenants || tenants.length === 0) {
    return (
      <View style={styles.noTenantBox}>
        <Text style={styles.noTenantText}>
          No tenants found. Please add tenants to proceed.
        </Text>
      </View>
    );
  }

  const currentData = selectedTenantId ? formData[selectedTenantId] : null;

  const getTenantName = (t) =>
    t?.app_user?.user?.first_name + " " + t?.app_user?.user?.last_name;

  return (
    <View style={{ marginTop: 10,marginBottom:10 }}>
      {/* Header */}
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>
          Tenant Details {tenants.length} of {tenants.length}
        </Text>

        <TouchableOpacity
          onPress={() => navigate("TenantDetails", { id: openflow_id })}
        >
          <Text style={styles.changeBtn}>Change tenant</Text>
        </TouchableOpacity>
      </View>

      {/* Tenant Cards */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.cardScroll}
      >
        {tenants.map((tenant) => {
          const id = String(tenant.app_user_id);
          const isActive = selectedTenantId === id;

          return (
            <TouchableOpacity
              key={id}
              style={[
                styles.tenantCard,
                isActive && styles.tenantCardActive,
              ]}
              onPress={() => setSelectedTenantId(id)}
            >
              <MaterialCommunityIcons
                name="account-circle"
                size={32}
                color="#7c4dff"
              />
              <View style={{ marginLeft: 10 }}>
                <Text style={styles.tenantRole}>
                  {tenant?.is_lead ? "Lead Tenant" : "Joint Tenant"}
                </Text>
                <Text style={styles.tenantName}>{getTenantName(tenant)}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Tenant Details Box */}
      {currentData && (
        <View style={styles.box}>
          {/* Title */}
          <View style={{ marginBottom: 12 }}>
            <Text style={styles.label}>Title</Text>
            <View style={styles.inputBox}>
              <Text>{currentData.title}</Text>
            </View>
          </View>

          {/* Name Row */}
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>First Name</Text>
              <View style={styles.inputBox}>
                <Text>{currentData.name}</Text>
              </View>
            </View>

            <View style={styles.col}>
              <Text style={styles.label}>Surname</Text>
              <View style={styles.inputBox}>
                <Text>{currentData.surname}</Text>
              </View>
            </View>
          </View>

          {/* Email */}
          <View style={{ marginTop: 12 }}>
            <Text style={styles.label}>Email Address</Text>
            <View style={styles.inputBox}>
              <Text>{currentData.email}</Text>
            </View>
          </View>

          {/* Code + Phone */}
          <View style={styles.row}>
            <View style={styles.colSmall}>
              <Text style={styles.label}>Code</Text>
              <View style={styles.inputBox}>
                <Text>{currentData.code}</Text>
              </View>
            </View>

            <View style={styles.colSmall}>
              <Text style={styles.label}>Contact Number</Text>
              <View style={styles.inputBox}>
                <Text>{currentData.phone}</Text>
              </View>
            </View>
          </View>

          {/* Address */}
          <Text style={[styles.label, { marginTop: 14 }]}>
            Address for contact after tenancy ends (optional)
          </Text>
          <TextInput
            placeholder="Start typing an address..."
            value={address}
            onChangeText={onAddressChange}
            style={styles.addressInput}
          />
        </View>
      )}
    </View>
  );
};

export default TenantRoleCards;

const styles = StyleSheet.create({
  noTenantBox: {
    padding: 14,
    borderWidth: 1,
    borderColor: "#D8D6E5",
    borderRadius: 8,
  },
  noTenantText: {
    textAlign: "center",
    color: "#777",
    fontSize: 14,
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  headerTitle: {
    fontSize: 16,
    fontWeight: "700",
  },

  changeBtn: {
    color: "#7c4dff",
    fontSize: 14,
    fontWeight: "600",
  },

  cardScroll: {
    paddingVertical: 6,
    gap: 10,
  },

  tenantCard: {
    borderWidth: 1,
    borderColor: "#e0dff2",
    borderRadius: 10,
    padding: 12,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",

    // Shadow
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },

  tenantCardActive: {
    borderColor: "#7c4dff",
    backgroundColor: "#f4edff",
  },

  tenantRole: {
    fontSize: 13,
    fontWeight: "700",
  },

  tenantName: {
    fontSize: 13,
    color: "#555",
    marginTop: 2,
  },

  box: {
    marginTop: 10,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#e2e1ef",

    shadowColor: "#000",
    shadowOpacity: 0.07,
    shadowRadius: 6,
    elevation: 2,
  },

  row: {
    flexDirection: "row",
    gap: 10,
    marginTop: 12,
  },

  col: { flex: 1 },

  colSmall: { flex: 0.48 },

  label: {
    fontSize: 13,
    fontWeight: "600",
    marginBottom: 4,
    color: "#444",
  },

  inputBox: {
    borderWidth: 1,
    borderColor: "#D4D2E2",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
  },

  addressInput: {
    borderWidth: 1,
    borderColor: "#D4D2E2",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginTop: 6,
    backgroundColor: "#fff",
  },
});
